class SiteNav extends HTMLElement {
  // --- Constants ---
  static PILL_TRANSITION = "transform 0.25s cubic-bezier(0.25, 0.1, 0.25, 1)";
  static STAGGER_BASE = 300;
  static STAGGER_PER_ITEM = 40;
  static ITEM_TRANSITION_MS = 250;
  static PILL_BUFFER = 30;
  static scrollThreshold = 50;

  constructor() {
    super();
  }

  connectedCallback() {
    this._menuToggle = this.querySelector("#menu-toggle");
    this._mobileMenuContent = this.querySelector("#mobile-menu-content");
    this._navElement =
      this.querySelector("nav.main-nav") || this.querySelector("nav");
    this._mobileNavDrawer = this.querySelector("#mobile-nav-drawer");
    this._menuLinks = this.querySelectorAll(".menu-link");
    this._menuPill = this.querySelector(".menu-pill");
    this._navOverlay = this.querySelector(".nav-overlay");
    this._openCartBtn = this.querySelector(".open-cart-btn");

    this._menuOpenTimeoutId = null;
    this._menuOpeningTimeoutId = null;
    this._themeColorTimeoutId = null;
    this._currentPillY = 0;

    this._bindEvents();
  }

  disconnectedCallback() {
    this._removeEvents();
  }

  // --- Helpers ---

  _getActiveLinkTargetY() {
    const activeLink =
      this._mobileMenuContent?.querySelector(".menu-link.active");
    if (!activeLink) return 0;
    return activeLink.offsetTop + activeLink.offsetHeight / 2 - 3;
  }

  _setPillTransform(x, y, animate) {
    if (!this._menuPill) return;
    this._menuPill.style.transition = animate
      ? SiteNav.PILL_TRANSITION
      : "none";
    this._menuPill.style.transform = `translate(${x}px, ${y}px)`;
  }

  _getActiveLinkFinishTime() {
    const activeLink =
      this._mobileMenuContent?.querySelector(".menu-link.active");
    if (!activeLink) return 560;
    const i =
      parseInt(getComputedStyle(activeLink).getPropertyValue("--i")) || 1;
    return (
      SiteNav.STAGGER_BASE +
      i * SiteNav.STAGGER_PER_ITEM +
      SiteNav.ITEM_TRANSITION_MS +
      SiteNav.PILL_BUFFER
    );
  }

  _setThemeColor(color) {
    const existing = document.querySelectorAll('meta[name="theme-color"]');
    existing.forEach((el) => el.remove());

    const meta = document.createElement("meta");
    meta.name = "theme-color";
    meta.content = color;
    document.head.appendChild(meta);

    document.documentElement.style.backgroundColor = color;
  }

  _getThemeColorInverse() {
    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue("--background-inverse")
        .trim() || "#324038"
    );
  }

  _getThemeColorPrimary() {
    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue("--background-primary")
        .trim() || "#ffffff"
    );
  }

  // --- Menu open / close ---

  closeMenu() {
    this._closeMenu();
  }

  openMenu() {
    this._openMenu();
  }

  _closeMenu() {
    const {
      _mobileMenuContent: menu,
      _navElement: nav,
      _mobileNavDrawer: drawer,
    } = this;
    if (!menu || !nav) return;
    if (
      !menu.classList.contains("active") &&
      !this.classList.contains("menu-open") &&
      !nav.classList.contains("menu-open")
    ) {
      return;
    }

    menu.classList.remove("active");
    this.classList.remove("menu-open", "menu-opening");
    nav.classList.remove("menu-open", "menu-opening");
    drawer?.classList.remove("menu-open", "menu-opening");
    document.documentElement.classList.remove("menu-open");
    document.body.classList.remove("menu-open");
    drawer?.setAttribute("aria-hidden", "true");
    this._menuToggle?.setAttribute("aria-expanded", "false");

    this.classList.add("menu-closing");
    nav.classList.add("menu-closing");
    drawer?.classList.add("menu-closing");

    if (this._themeColorTimeoutId) {
      clearTimeout(this._themeColorTimeoutId);
      this._themeColorTimeoutId = null;
    }
    this._themeColorTimeoutId = setTimeout(() => {
      this._setThemeColor(this._getThemeColorPrimary());
      this._themeColorTimeoutId = null;
    }, 400);

    if (this._menuOpenTimeoutId) {
      clearTimeout(this._menuOpenTimeoutId);
      this._menuOpenTimeoutId = null;
    }
    if (this._menuOpeningTimeoutId) {
      clearTimeout(this._menuOpeningTimeoutId);
      this._menuOpeningTimeoutId = null;
    }

    this._menuOpeningTimeoutId = setTimeout(() => {
      this.classList.remove("menu-closing");
      nav.classList.remove("menu-closing");
      drawer?.classList.remove("menu-closing");
      this._menuOpeningTimeoutId = null;
    }, 1000);

    const activeLink = menu?.querySelector(".menu-link.active");
    if (activeLink) {
      activeLink.style.transition =
        "opacity 0.25s cubic-bezier(0.25, 0.1, 0.25, 1), transform 0.25s cubic-bezier(0.25, 0.1, 0.25, 1)";
      activeLink.style.transform = "translate(0, 0)";
      activeLink.style.opacity = "0.7";
    }

    this._setPillTransform(-50, this._currentPillY, true);

    setTimeout(() => {
      this.classList.remove("pill-visible");
      nav.classList.remove("pill-visible");
      drawer?.classList.remove("pill-visible");
      if (activeLink) {
        activeLink.style.transition = "";
        activeLink.style.transform = "";
        activeLink.style.opacity = "";
      }
    }, 250);
  }

  _openMenu() {
    const {
      _mobileMenuContent: menu,
      _navElement: nav,
      _mobileNavDrawer: drawer,
    } = this;

    if (this._menuOpenTimeoutId) {
      clearTimeout(this._menuOpenTimeoutId);
      this._menuOpenTimeoutId = null;
    }
    if (this._menuOpeningTimeoutId) {
      clearTimeout(this._menuOpeningTimeoutId);
      this._menuOpeningTimeoutId = null;
    }
    if (this._themeColorTimeoutId) {
      clearTimeout(this._themeColorTimeoutId);
      this._themeColorTimeoutId = null;
    }

    menu.classList.add("active");
    this.classList.add("menu-open");
    nav.classList.add("menu-open");
    drawer?.classList.add("menu-open");
    document.documentElement.classList.add("menu-open");
    document.body.classList.add("menu-open");
    drawer?.setAttribute("aria-hidden", "false");
    this._menuToggle?.setAttribute("aria-expanded", "true");

    this.classList.remove("menu-closing");
    nav.classList.remove("menu-closing");
    drawer?.classList.remove("menu-closing");

    this.classList.add("menu-opening");
    nav.classList.add("menu-opening");
    drawer?.classList.add("menu-opening");

    this._setThemeColor(this._getThemeColorInverse());

    this._menuOpeningTimeoutId = setTimeout(() => {
      this.classList.remove("menu-opening");
      nav.classList.remove("menu-opening");
      drawer?.classList.remove("menu-opening");
      this._menuOpeningTimeoutId = null;
    }, 1000);

    const targetY = this._getActiveLinkTargetY();
    this._currentPillY = targetY;
    this._setPillTransform(-50, targetY, false);

    const revealDelay = this._getActiveLinkFinishTime();
    this._menuOpenTimeoutId = setTimeout(() => {
      if (menu.classList.contains("active")) {
        this._setPillTransform(0, targetY, true);
        this.classList.add("pill-visible");
        nav.classList.add("pill-visible");
        drawer?.classList.add("pill-visible");
      }
    }, revealDelay);
  }

  // --- Event binding ---

  _onToggleClick = () => {
    const isOpen = this._mobileMenuContent.classList.contains("active");
    isOpen ? this._closeMenu() : this._openMenu();
  };

  _onOverlayClick = () => {
    if (this._mobileMenuContent?.classList.contains("active")) {
      this._closeMenu();
    }
  };

  _onKeydown = (e) => {
    if (
      (e.key === "Escape" || e.key === "Esc") &&
      this._mobileMenuContent?.classList.contains("active")
    ) {
      this._closeMenu();
      document.activeElement?.blur();
    }
  };

  _onResize = () => {
    if (window.innerWidth >= 768) {
      if (this._mobileMenuContent?.classList.contains("active")) {
        this._closeMenu();
      }
    } else {
      if (this._mobileMenuContent?.classList.contains("active")) {
        const targetY = this._getActiveLinkTargetY();
        this._currentPillY = targetY;
        this._setPillTransform(0, targetY, false);
      }
    }
  };

  _bindEvents() {
    if (this._menuToggle && this._mobileMenuContent && this._navElement) {
      this._menuToggle.addEventListener("click", this._onToggleClick);
    }

    this._navOverlay?.addEventListener("click", this._onOverlayClick);

    this._menuLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();

        if (link.classList.contains("active")) {
          this._closeMenu();
          return;
        }

        const newTargetY = link.offsetTop + link.offsetHeight / 2 - 3;

        this._setPillTransform(-50, this._currentPillY, true);

        const oldActiveLink =
          this._mobileMenuContent?.querySelector(".menu-link.active");
        oldActiveLink?.classList.remove("active");

        setTimeout(() => {
          this._currentPillY = newTargetY;
          this._setPillTransform(-50, newTargetY, false);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this._setPillTransform(0, newTargetY, true);
              link.classList.add("active");

              setTimeout(() => {
                this._closeMenu();
              }, 300);
            });
          });
        }, 250);

        const url = link.href;
        window.location.href = url;
      });
    });

    if (this._openCartBtn) {
      this._openCartBtn.addEventListener("click", (e) => {
        e.preventDefault();
        const isMenuOpen =
          this._mobileMenuContent?.classList.contains("active") ||
          this._navElement?.classList.contains("menu-open");

        if (isMenuOpen) {
          this._closeMenu();
          const main = document.querySelector("main");
          if (!main?.classList.contains("cart-open")) {
            if (window.openCart) window.openCart();
          }
          return;
        }

        const main = document.querySelector("main");
        if (main?.classList.contains("cart-open")) {
          if (window.closeCart) window.closeCart();
        } else {
          if (window.openCart) window.openCart();
        }
      });
    }
    this.handleScroll();

    window.addEventListener("resize", this._onResize);
    document.addEventListener("keydown", this._onKeydown);
  }

  _removeEvents() {
    this._menuToggle?.removeEventListener("click", this._onToggleClick);
    this._navOverlay?.removeEventListener("click", this._onOverlayClick);
    window.removeEventListener("resize", this._onResize);
    document.removeEventListener("keydown", this._onKeydown);
  }

  handleScroll = () => {
    if (window.scrollY > SiteNav.scrollThreshold) {
      this._navElement.classList.add("scrolled");
    } else {
      this._navElement.classList.remove("scrolled");
    }
  };
}

customElements.define("site-nav", SiteNav);

// Global helper functions for mobile nav
window.closeMobileMenu = () => {
  const siteNav = document.querySelector("site-nav");
  if (siteNav && typeof siteNav.closeMenu === "function") {
    siteNav.closeMenu();
  }
};

window.openMobileMenu = () => {
  const siteNav = document.querySelector("site-nav");
  if (siteNav && typeof siteNav.openMenu === "function") {
    siteNav.openMenu();
  }
};
